import type { PropertyListings } from "@/types/PropertyListings";


class Helper {
    static APP_TIME_ZONE = 'Asia/Kuala_Lumpur';

    static parseBackendDate = (value: string | Date | null | undefined) => {
        if (!value) return null;
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }

        const normalized = value.includes('T')
            ? value
            : value.replace(' ', 'T');
        const hasExplicitZone = /([zZ]|[+\-]\d{2}:\d{2})$/.test(normalized);
        const date = new Date(hasExplicitZone ? normalized : `${normalized}Z`);
        return Number.isNaN(date.getTime()) ? null : date;
    };

    static formatDateTime = (value: string | Date | null | undefined, options?: Intl.DateTimeFormatOptions) => {
        const date = Helper.parseBackendDate(value);
        if (!date) return '-';
        return new Intl.DateTimeFormat('en-MY', {
            timeZone: Helper.APP_TIME_ZONE,
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            ...options,
        }).format(date);
    };

    static formatDateOnly = (value: string | Date | null | undefined, options?: Intl.DateTimeFormatOptions) => {
        const date = Helper.parseBackendDate(value);
        if (!date) return '-';
        return new Intl.DateTimeFormat('en-MY', {
            timeZone: Helper.APP_TIME_ZONE,
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            ...options,
        }).format(date);
    };

    static formatTimeOnly = (value: string | Date | null | undefined, options?: Intl.DateTimeFormatOptions) => {
        const date = Helper.parseBackendDate(value);
        if (!date) return '-';
        return new Intl.DateTimeFormat('en-MY', {
            timeZone: Helper.APP_TIME_ZONE,
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            ...options,
        }).format(date);
    };

    static allowOnlyNumbers = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (
            ["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight"].includes(e.key)
        ) {
            return;
        }
    
        if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) {
            return;
        }
    
        if (!/^[0-9]$/.test(e.key)) {
            e.preventDefault();
        }
    };

    static formatCurrency = (amount: number, currency: string = "MYR", locale: string = "en-MY") => {
        if (isNaN(amount)) return "0.00";
        return amount.toLocaleString(locale, {
            style: "currency",
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    static formatWithDecimal = (value: number, locale: string = "en-MY") => {
        if (isNaN(value)) return "0.00";
        return value.toLocaleString(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };
      
    static formatWithoutDecimal = (value: number, locale: string = "en-MY") => {
        if (isNaN(value)) return "0";
        return value.toLocaleString(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    };

    static errorHandler = (err: any): string => {
        if (err?.response?.data?.error) return err.response.data.error;
        if (err?.message) return err.message;
        return 'Something went wrong';
    };

    static slugify = (str: string) => {
        return str
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-') 
        .replace(/^-+|-+$/g, '');
    }

    static capitalizeFirstLetter = (str: string): string => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    static handleFocusToEnd = (e: any) => {
        const input = e.target;
        const length = input.value.length;
        setTimeout(() => {
            input.setSelectionRange(length, length);
        }, 10)
    };

    static handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/[^0-9]/g, '');
        if (val === '') {
          return '0.00';
        }
        let num = parseFloat(val) / 100;
        return num.toFixed(2);
    };

    // Must stay in sync with the public site's canonical listing path
    // (xpillar `buildListingPathFromParts`): /properties/{type}/{state}/{headline}-{id}.
    // There is no for-sale/for-rent segment — adding one 404s.
    static LISTING_TYPE_SLUGS: Record<number, string> = {
        1: 'residential',
        2: 'commercial',
        3: 'industrial',
        4: 'agricultural',
        5: 'others',
    };

    /**
     * The trailing id in a public listing URL is the *property* id, never the
     * listing id: the two share one number space, so `listing.id` resolves to
     * a different property or to nothing at all (404). Everything else in the
     * path comes off the property too, because that is what the public page
     * builds its canonical from - reading them off the listing only earns a
     * 301 on every click.
     */
    static buildListingUrl = (listing: PropertyListings) => {
        const property = listing.property;
        const type = Helper.LISTING_TYPE_SLUGS[property?.listingTypeId ?? listing.listingTypeId] ?? 'others';
        const stateName = property?.state?.name ?? listing.state?.name ?? '';
        const headline = property?.headline ?? listing.headline;
        const propertyId = listing.propertyId ?? property?.id;
        return `/properties/${type}/${Helper.slugify(stateName)}/${Helper.slugify(headline)}-${propertyId}`;
    }

    static convertNumberToWords(amount: number): string {
        if (amount === 0) return 'Zero Only';
    
        const ones = [
            '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'
        ];
        const teens = [
            'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 
            'Seventeen', 'Eighteen', 'Nineteen'
        ];
        const tens = [
            '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'
        ];
        const thousands = [
            '', 'Thousand', 'Million', 'Billion', 'Trillion'
        ];
    
        function chunkToWords(num: number): string {
            let words = '';
    
            if (num >= 100) {
                words += ones[Math.floor(num / 100)] + ' Hundred ';
                num %= 100;
            }
    
            if (num >= 20) {
                words += tens[Math.floor(num / 10)] + ' ';
                num %= 10;
            } else if (num >= 10) {
                words += teens[num - 10] + ' ';
                num = 0;
            }
    
            if (num > 0) {
                words += ones[num] + ' ';
            }
    
            return words.trim();
        }
        const safeAmount = Number(amount ?? 0);
        const [ringgitPart, senPart] = safeAmount.toFixed(2).split('.');
        let ringgit = parseInt(ringgitPart);
        let sen = parseInt(senPart);
    
        let result = '';
        let i = 0;
    
        if (ringgit === 0) {
            result = 'Zero';
        } else {
            while (ringgit > 0) {
                const chunk = ringgit % 1000;
                if (chunk > 0) {
                    result = `${chunkToWords(chunk)} ${thousands[i]} ${result}`.trim();
                }
                ringgit = Math.floor(ringgit / 1000);
                i++;
            }
        }
    
        if (sen > 0) {
            result += ` And ${chunkToWords(sen)} Sen`;
        }
        result += ' Only';
        return result.trim();
    }

    static isValidEmail = (email: string): boolean => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email.trim());
    };

    static isImage = (url: string) => {
        if (!url) return false;
        return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
    };

    static formatNumber = (value: number) => {
        return value.toLocaleString("en-MY", {
            style: "decimal",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }
}

export default Helper;
